import React, { useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import FolderIcon from "@mui/icons-material/Folder";
import { db } from "../firebaseConfig";
import {
  collection,
  query,
  where,
  getDocs,
  deleteDoc,
  updateDoc,
  doc,
  limit,
  startAfter,
  setDoc,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore";

const SEARCH_FIELDS = [
  { value: "document_type", label: "Belge Tipi" },
  { value: "user_id",       label: "Kullanıcı ID" },
  { value: "user_name",     label: "Kullanıcı Adı" },
  { value: "school_name",   label: "Okul Adı" },
];

const PAGE_SIZE = 50;

// Düzenlenebilir alanlar (id ve created_at hariç)
const EDITABLE_FIELDS = ["document_type", "user_name", "user_id", "school_name", "city", "district", "screen_id"];

const ManageDocuments = () => {
  const [searchField, setSearchField] = useState("document_type");
  const [searchValue, setSearchValue] = useState("");
  const [documents, setDocuments] = useState([]);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Silme
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Düzenleme
  const [editTarget, setEditTarget] = useState(null);
  const [editFields, setEditFields] = useState({});
  const [saving, setSaving] = useState(false);

  const buildQuery = (after = null) => {
    const value = searchValue.trim();
    // Excel'den yüklenen user_id bazı belgelerde sayı, bazılarında metin olarak kayıtlı.
    // Firestore tip duyarlı olduğu için iki tipi de arıyoruz.
    const variants = [value];
    if (value !== "" && !isNaN(value)) variants.push(Number(value));
    const constraints = [
      variants.length > 1
        ? where(searchField, "in", variants)
        : where(searchField, "==", value),
      limit(PAGE_SIZE),
    ];
    if (after) constraints.push(startAfter(after));
    return query(collection(db, "documents"), ...constraints);
  };

  const handleSearch = async () => {
    if (!searchValue.trim()) return;
    setLoading(true);
    setError("");
    setDocuments([]);
    setLastDoc(null);
    setHasMore(false);
    setSearched(false);
    try {
      const snapshot = await getDocs(buildQuery());
      const docs = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setDocuments(docs);
      setLastDoc(snapshot.docs[snapshot.docs.length - 1] ?? null);
      setHasMore(snapshot.docs.length === PAGE_SIZE);
      setSearched(true);
    } catch (err) {
      console.error("Firestore arama hatası:", err);
      setError("Arama sırasında hata oluştu: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadMore = async () => {
    if (!lastDoc) return;
    setLoadingMore(true);
    try {
      const snapshot = await getDocs(buildQuery(lastDoc));
      const more = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setDocuments((prev) => [...prev, ...more]);
      setLastDoc(snapshot.docs[snapshot.docs.length - 1] ?? null);
      setHasMore(snapshot.docs.length === PAGE_SIZE);
    } catch (err) {
      setError("Yüklenirken hata oluştu: " + err.message);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  // --- Silme ---
  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteDoc(doc(db, "documents", deleteTarget.id));
      setDocuments((prev) => prev.filter((d) => d.id !== deleteTarget.id));
      setSuccess(`Belge silindi.`);
    } catch (err) {
      setError("Silme sırasında hata oluştu: " + err.message);
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  // --- Düzenleme ---
  // Kullanıcı ekranı belgeleri screens/{screen_id}.documents_ids listesinden okur;
  // belgenin screen_id'si değişince bu listeler de güncellenmeli.
  const syncScreens = async (docId, fields) => {
    // IBB belgeleri screens koleksiyonuna eklenmiyor (bkz. AdminPage)
    if (docId.toLowerCase().endsWith("ibb")) return;
    const newScreenId = String(fields.screen_id ?? "").trim();

    const oldScreens = await getDocs(
      query(collection(db, "screens"), where("documents_ids", "array-contains", docId))
    );
    await Promise.all(
      oldScreens.docs
        .filter((s) => s.id !== newScreenId)
        .map((s) => updateDoc(s.ref, { documents_ids: arrayRemove(docId) }))
    );

    if (newScreenId) {
      await setDoc(
        doc(db, "screens", newScreenId),
        { user_id: String(fields.user_id ?? ""), documents_ids: arrayUnion(docId) },
        { merge: true }
      );
    }
  };

  const handleEditOpen = (d) => {
    setEditTarget(d);
    const fields = {};
    EDITABLE_FIELDS.forEach((key) => {
      fields[key] = d[key] ?? "";
    });
    setEditFields(fields);
  };

  const handleEditSave = async () => {
    if (!editTarget) return;
    setSaving(true);
    try {
      const ref = doc(db, "documents", editTarget.id);
      await updateDoc(ref, editFields);
      await syncScreens(editTarget.id, editFields);
      setDocuments((prev) =>
        prev.map((d) => (d.id === editTarget.id ? { ...d, ...editFields } : d))
      );
      setSuccess("Belge güncellendi.");
      setEditTarget(null);
    } catch (err) {
      setError("Güncelleme sırasında hata oluştu: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ width: "100%", maxWidth: 1100, mx: "auto", p: 3 }}>
      <Typography variant="h5" sx={{ mb: 1, fontWeight: "bold", color: "#582b70" }}>
        Belgeleri Yönet
      </Typography>
      <Typography variant="body2" sx={{ mb: 3, color: "text.secondary" }}>
        Arama yapıldığında ilgili belgeler yüklenir. Her seferinde en fazla {PAGE_SIZE} kayıt getirilir.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>{success}</Alert>
      )}

      {/* Arama satırı */}
      <Box sx={{ display: "flex", gap: 1.5, mb: 3, alignItems: "center" }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <Select
            value={searchField}
            onChange={(e) => {
              setSearchField(e.target.value);
              setDocuments([]);
              setSearched(false);
              setSearchValue("");
              setHasMore(false);
            }}
          >
            {SEARCH_FIELDS.map((f) => (
              <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          placeholder={`${SEARCH_FIELDS.find((f) => f.value === searchField)?.label} girin...`}
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          onKeyDown={handleKeyDown}
          size="small"
          sx={{ flex: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#582b70", fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
        />

        <Button
          variant="contained"
          onClick={handleSearch}
          disabled={loading || !searchValue.trim()}
          sx={{
            backgroundColor: "#582b70",
            "&:hover": { backgroundColor: "#4a245f" },
            textTransform: "none",
            whiteSpace: "nowrap",
          }}
        >
          {loading ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Ara"}
        </Button>
      </Box>

      {/* Boş durumlar */}
      {!searched && !loading && (
        <Box sx={{ textAlign: "center", py: 8, color: "text.disabled" }}>
          <SearchIcon sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
          <Typography>Arama yaparak belgeleri listeleyin.</Typography>
        </Box>
      )}

      {searched && documents.length === 0 && (
        <Box sx={{ textAlign: "center", py: 8, color: "text.disabled" }}>
          <FolderIcon sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
          <Typography>Sonuç bulunamadı.</Typography>
        </Box>
      )}

      {/* Tablo */}
      {documents.length > 0 && (
        <>
          <Paper elevation={1} sx={{ borderRadius: 2, overflow: "hidden" }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f5f0f8" }}>
                    <TableCell sx={{ fontWeight: "bold", color: "#582b70" }}>Belge ID</TableCell>
                    <TableCell sx={{ fontWeight: "bold", color: "#582b70" }}>Belge Tipi</TableCell>
                    <TableCell sx={{ fontWeight: "bold", color: "#582b70" }}>Kullanıcı</TableCell>
                    <TableCell sx={{ fontWeight: "bold", color: "#582b70" }}>Okul</TableCell>
                    <TableCell sx={{ fontWeight: "bold", color: "#582b70" }}>Şehir / İlçe</TableCell>
                    <TableCell align="center" sx={{ fontWeight: "bold", color: "#582b70" }}>İşlem</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {documents.map((d, i) => (
                    <TableRow
                      key={d.id}
                      sx={{
                        backgroundColor: i % 2 === 0 ? "#fff" : "#faf7fc",
                        "&:hover": { backgroundColor: "#f0e8f5" },
                      }}
                    >
                      <TableCell>
                        <Typography
                          variant="caption"
                          sx={{ fontFamily: "monospace", fontSize: "0.7rem", color: "text.secondary", wordBreak: "break-all" }}
                        >
                          {d.id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {d.document_type ? (
                          <Chip
                            label={d.document_type}
                            size="small"
                            sx={{ backgroundColor: "#ede0f5", color: "#582b70", fontWeight: "bold", fontSize: "0.7rem" }}
                          />
                        ) : (
                          <Typography variant="caption" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{d.user_name ?? "—"}</Typography>
                        {d.user_id && (
                          <Typography variant="caption" color="text.secondary">ID: {d.user_id}</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>{d.school_name ?? "—"}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                          {[d.city, d.district].filter(Boolean).join(" / ") || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
                          <Tooltip title="Düzenle">
                            <IconButton size="small" onClick={() => handleEditOpen(d)} sx={{ color: "#582b70" }}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Sil">
                            <IconButton size="small" onClick={() => setDeleteTarget(d)} sx={{ color: "#d32f2f" }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ px: 2, py: 1, borderTop: "1px solid #ede0f5", backgroundColor: "#faf7fc" }}>
              <Typography variant="caption" color="text.secondary">
                {documents.length} belge yüklendi
              </Typography>
            </Box>
          </Paper>

          {/* Daha Fazla Yükle */}
          {hasMore && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <Button
                variant="outlined"
                onClick={handleLoadMore}
                disabled={loadingMore}
                sx={{
                  borderColor: "#582b70",
                  color: "#582b70",
                  textTransform: "none",
                  "&:hover": { borderColor: "#4a245f", backgroundColor: "#f5f0f8" },
                }}
              >
                {loadingMore ? (
                  <CircularProgress size={20} sx={{ color: "#582b70" }} />
                ) : (
                  `Daha Fazla Yükle (${PAGE_SIZE}'er)`
                )}
              </Button>
            </Box>
          )}
        </>
      )}

      {/* Silme diyalogu */}
      <Dialog open={!!deleteTarget} onClose={() => !deleting && setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: "#582b70", fontWeight: "bold" }}>Belgeyi Sil</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Bu belgeyi silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
          </DialogContentText>
          {deleteTarget && (
            <Paper sx={{ mt: 2, p: 1.5, backgroundColor: "#fef3f2", borderRadius: 1 }}>
              <Typography variant="caption" sx={{ fontFamily: "monospace", wordBreak: "break-all", color: "#b91c1c" }}>
                {deleteTarget.id}
              </Typography>
              {deleteTarget.document_type && (
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  Tip: <strong>{deleteTarget.document_type}</strong>
                </Typography>
              )}
            </Paper>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting} sx={{ textTransform: "none", color: "text.secondary" }}>
            İptal
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            disabled={deleting}
            variant="contained"
            color="error"
            startIcon={deleting ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <DeleteIcon />}
            sx={{ textTransform: "none" }}
          >
            {deleting ? "Siliniyor..." : "Sil"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Düzenleme diyalogu */}
      <Dialog open={!!editTarget} onClose={() => !saving && setEditTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: "#582b70", fontWeight: "bold" }}>Belgeyi Düzenle</DialogTitle>
        <DialogContent>
          <Typography variant="caption" sx={{ fontFamily: "monospace", color: "text.secondary", wordBreak: "break-all" }}>
            {editTarget?.id}
          </Typography>
          <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
            {EDITABLE_FIELDS.map((key) => (
              <TextField
                key={key}
                label={key}
                size="small"
                fullWidth
                value={editFields[key] ?? ""}
                onChange={(e) => setEditFields((prev) => ({ ...prev, [key]: e.target.value }))}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditTarget(null)} disabled={saving} sx={{ textTransform: "none", color: "text.secondary" }}>
            İptal
          </Button>
          <Button
            onClick={handleEditSave}
            disabled={saving}
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <EditIcon />}
            sx={{ textTransform: "none", backgroundColor: "#582b70", "&:hover": { backgroundColor: "#4a245f" } }}
          >
            {saving ? "Kaydediliyor..." : "Kaydet"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManageDocuments;
